// Settings → Account → Time zone (time-zone PR): "Automatic (this device)" or a zone
// picked from the world list. Saved on the account, so every device and the admin
// pages show times in it. The last change wins (the user here, or an admin).

import { useState } from 'react';
import { ApiError, saveSettings, type User } from '../api';
import { deviceZone, pickedZoneUnknown, zoneTitle, zoneWithOffset } from '../time';
import { TimeZonePicker } from './TimeZonePicker';

/** "Automatic (this device): Eastern Time" or "Karachi (UTC+05:00)". */
export function zoneChoiceText(zone: string | null | undefined, now: number = Date.now()): string {
  return zone ? zoneWithOffset(zone, now) : `Automatic (this device): ${zoneTitle(deviceZone())}`;
}

export function TimeZoneSetting({ user, onSaved }: { user: User; onSaved: (u: User) => void }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ tone: 'ok' | 'bad'; text: string } | null>(null);
  const zone = user.timeZone ?? null;

  const pick = async (next: string | null) => {
    setBusy(true);
    setMessage(null);
    try {
      const { user: u } = await saveSettings({ timeZone: next });
      onSaved(u);
      setOpen(false);
      setMessage({ tone: 'ok', text: next ? `Saved. Times now show in ${zoneTitle(next)}.` : 'Saved. Times follow the time zone of each device.' });
    } catch (err) {
      setMessage({ tone: 'bad', text: err instanceof ApiError ? err.message : 'Couldn’t save. Try again.' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="setting setting-stack">
      <div className="setting-head">
        <div className="setting-text">
          <span className="setting-name" id="tz-label">
            Time zone
          </span>
          <span className="setting-help">{zoneChoiceText(zone)}</span>
          {pickedZoneUnknown() && (
            <span className="setting-help setting-warn" role="status">
              This browser doesn’t know the time zone saved on your account ({zone}), so it shows times in this device’s zone. Updating the browser fixes it.
            </span>
          )}
        </div>
        <button
          className="btn btn-small"
          type="button"
          aria-expanded={open}
          onClick={() => {
            setOpen((v) => !v);
            setMessage(null);
          }}
        >
          {open ? 'Close' : 'Change'}
        </button>
      </div>
      {open && <TimeZonePicker value={zone} busy={busy} onPick={(z) => void pick(z)} onCancel={() => setOpen(false)} />}
      <p className="setting-note">Times in your call history, voicemail and the admin pages use this zone.</p>
      {message && (
        <p className={`setting-msg setting-msg-${message.tone}`} role={message.tone === 'bad' ? 'alert' : 'status'}>
          {message.text}
        </p>
      )}
    </div>
  );
}
